import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Thermometer, 
  ShieldCheck, 
  AlertTriangle, 
  FileText,
  Scan,
  TrendingUp,
  FileCheck,
  Package,
  Snowflake,
  Scale,
  Sprout,
  RefreshCw,
  ArrowRight,
  Download,
  X,
  Diamond,
  Menu,
  ChevronDown,
  Sun,
  Moon
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { QuantTechGridLogo } from "@/components/Logo";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component
const Navigation = ({ onRequestPilot }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border" data-testid="main-navigation">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div data-testid="logo">
            <QuantTechGridLogo className="cursor-pointer" />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('capabilities')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-what-we-do">What We Do</button>
            <button onClick={() => scrollToSection('process')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-how-it-works">How It Works</button>
            <button onClick={() => scrollToSection('for-whom')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-for-whom">For Whom</button>
            <button onClick={() => scrollToSection('afi-index')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-framework">Framework</button>
          </div>

          {/* Theme Toggle + CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <ThemeToggle />
            <button 
              onClick={onRequestPilot}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-display font-semibold text-sm tracking-wider uppercase px-5 py-2.5 transition-colors"
              data-testid="nav-request-pilot-btn"
            >
              Request Structured Pilot
            </button>
          </div>

          {/* Mobile: Theme Toggle + Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            <ThemeToggle />
            <button 
              className="text-foreground p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button onClick={() => scrollToSection('capabilities')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors text-left">What We Do</button>
              <button onClick={() => scrollToSection('process')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors text-left">How It Works</button>
              <button onClick={() => scrollToSection('for-whom')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors text-left">For Whom</button>
              <button onClick={() => scrollToSection('afi-index')} className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors text-left">Framework</button>
              <button 
                onClick={() => { onRequestPilot(); setMobileMenuOpen(false); }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-display font-semibold text-sm tracking-wider uppercase px-5 py-2.5 transition-colors mt-2"
              >
                Request Pilot
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onRequestPilot }) => {
  return (
    <section className="min-h-screen relative flex items-center bg-background overflow-hidden pt-20 noise-overlay" data-testid="hero-section">
      {/* Grid Background */}
      <div className="absolute inset-0 grid-pattern opacity-50" />
      
      <div className="relative z-10 w-full px-8 md:px-16 lg:px-24 py-20 md:py-32">
        <div className="max-w-5xl">
          <h1 className="font-display font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground uppercase leading-[0.9] tracking-tight mb-8" data-testid="hero-title">
            QuantTechGrid<sup className="text-[0.5em] align-super">™</sup> — The Risk Intelligence Layer for India's Cold Chain.
          </h1>
          
          <p className="font-body text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-10" data-testid="hero-description">
            Reducing post-harvest losses and strengthening export confidence by transforming environmental exposure data into structured, defensible intelligence.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              onClick={onRequestPilot}
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-display font-medium tracking-wider uppercase px-6 py-3.5 transition-colors flex items-center justify-center gap-2 group text-sm amber-glow"
              data-testid="hero-request-pilot-btn"
            >
              Request Structured Pilot
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              className="border border-border hover:border-muted-foreground text-foreground font-display font-medium tracking-wider uppercase px-6 py-3.5 transition-colors flex items-center justify-center gap-2 text-sm"
              data-testid="hero-download-btn"
            >
              <Download className="w-4 h-4" />
              Download Exposure Framework Brief
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Capabilities Section
const CapabilitiesSection = () => {
  const capabilities = [
    {
      icon: Thermometer,
      number: "01",
      title: "Environmental Exposure Modeling",
      description: "Transforms temperature, humidity, and time data into cumulative exposure impact using internationally referenced shelf-life science."
    },
    {
      icon: ShieldCheck,
      number: "02",
      title: "Insurance-Ready Impact Quantification",
      description: "Converts environmental exposure into structured commercial impact summaries for claim defensibility and underwriting clarity."
    },
    {
      icon: AlertTriangle,
      number: "03",
      title: "Structured Risk Alerts",
      description: "Identifies cumulative risk conditions early — enabling operational intervention before irreversible degradation occurs."
    },
    {
      icon: FileText,
      number: "04",
      title: "Defensible Evidence Documentation",
      description: "Generates tamper-evident, audit-ready exposure summaries aligned with exporter, buyer, and insurer documentation standards."
    }
  ];

  return (
    <section id="capabilities" className="py-20 md:py-32 bg-background" data-testid="capabilities-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Capabilities</span>
            <div className="h-px w-16 bg-border" />
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight" data-testid="capabilities-title">
            From Telemetry to Structured<br />Exposure Intelligence
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {capabilities.map((cap, index) => (
            <div 
              key={index}
              className="bg-card border border-border hover:border-primary/50 transition-colors duration-300 p-6 lg:p-8 flex flex-col gap-6 group"
              data-testid={`capability-card-${index + 1}`}
            >
              <div className="flex items-start justify-between">
                <cap.icon className="w-5 h-5 text-primary" />
                <span className="font-display text-sm text-muted-foreground">{cap.number}</span>
              </div>
              <div>
                <h3 className="font-display font-medium text-lg text-foreground mb-3">{cap.title}</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{cap.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Accountability Gap Section
const AccountabilityGapSection = () => {
  const results = [
    "Product quality degradation",
    "Export rejections and disputes",
    "Invisible financial leakage",
    "Insurance claim friction",
    "Loss of trust across the value chain"
  ];

  return (
    <section className="py-20 md:py-32 bg-background" data-testid="accountability-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">The Accountability Gap</span>
              <div className="h-px w-16 bg-border" />
            </div>
            
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight leading-[0.95] mb-6" data-testid="accountability-title">
              India's Cold Chain Is Expanding — Accountability Has Not.
            </h2>
            
            <p className="font-body text-primary mb-6">
              QuantTechGrid<sup className="text-[0.5em] align-super">™</sup> builds the standardized exposure layer to close this gap.
            </p>
            
            <p className="font-body text-muted-foreground leading-relaxed mb-6">
              India loses a significant share of perishable produce — not because data doesn't exist, but because it isn't interpreted, standardized, or made defensible.
            </p>
            
            <p className="font-body text-muted-foreground leading-relaxed mb-8">
              Cold storage and reefer infrastructure are expanding rapidly. Sensors are everywhere. Data is being collected continuously.
            </p>
            
            <p className="font-body text-muted-foreground mb-4">Yet most environmental data today is:</p>
            
            <div className="space-y-3">
              <p className="font-body text-muted-foreground"><span className="text-foreground font-medium">Monitored</span> — but not interpreted</p>
              <p className="font-body text-muted-foreground"><span className="text-foreground font-medium">Recorded</span> — but not standardized</p>
              <p className="font-body text-muted-foreground"><span className="text-foreground font-medium">Available</span> — but not legally or commercially defensible</p>
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">The Result</span>
            </div>
            
            <div className="space-y-3">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className="bg-card border border-border p-5 data-line"
                  data-testid={`result-card-${index + 1}`}
                >
                  <p className="font-body text-card-foreground">{result}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Capture Environmental Telemetry",
      description: "Continuous temperature and humidity capture across transit corridors.",
      subtext: "IoT sensor integration across the full cold-chain transit corridor."
    },
    {
      number: "02",
      title: "Apply Scientific Exposure Modeling",
      description: "Cumulative degradation modeling based on commodity-specific sensitivity parameters.",
      subtext: "Internationally referenced shelf-life science applied to raw telemetry streams."
    },
    {
      number: "03",
      title: "Generate Defensible Documentation",
      description: "Structured exposure summaries for exporters, buyers, insurers, and regulators.",
      subtext: "Audit-ready, tamper-evident output designed for multi-stakeholder review."
    }
  ];

  return (
    <section id="process" className="py-20 md:py-32 bg-background" data-testid="process-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Process</span>
            <div className="h-px w-16 bg-border" />
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight" data-testid="process-title">
            How Structured Exposure<br />Intelligence Works
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="bg-card border border-border p-6 lg:p-8 flex flex-col"
              data-testid={`process-step-${index + 1}`}
            >
              <div className="inline-flex items-center justify-center w-10 h-10 border border-primary/50 text-primary font-display font-medium text-sm mb-8">
                {step.number}
              </div>
              <h3 className="font-display font-medium text-lg text-foreground mb-4">{step.title}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed mb-4">{step.description}</p>
              <p className="font-body text-xs text-muted-foreground/70 leading-relaxed mt-auto">{step.subtext}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// AFI Index Section
const AFIIndexSection = () => {
  const attributes = [
    {
      label: "Cumulative",
      description: "Time-integrated exposure, not point-in-time readings"
    },
    {
      label: "Structured",
      description: "Standardized output format for cross-party use"
    },
    {
      label: "Defensible",
      description: "Audit-trail backed interpretation methodology"
    }
  ];

  return (
    <section id="afi-index" className="py-20 md:py-32 bg-background" data-testid="afi-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {/* Left Column */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Index</span>
              <div className="h-px w-16 bg-border" />
            </div>
            <div className="font-display font-bold text-8xl md:text-9xl text-primary/20 tracking-tight" data-testid="afi-logo">
              AFI
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-8">
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight mb-6" data-testid="afi-title">
              Agri-Freshness Integrity Index (AFI)
            </h2>
            
            <p className="font-body text-muted-foreground leading-relaxed mb-10 max-w-2xl">
              A structured environmental exposure index that interprets cumulative temperature impact across time — enabling consistent risk communication across stakeholders.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {attributes.map((attr, index) => (
                <div 
                  key={index}
                  className="bg-card border border-border p-6"
                  data-testid={`afi-attribute-${index + 1}`}
                >
                  <span className="font-display font-medium text-sm tracking-widest text-primary uppercase block mb-3">{attr.label}</span>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">{attr.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// For Whom Section
const ForWhomSection = () => {
  const audiences = [
    {
      icon: Package,
      title: "Exporters",
      description: "Reduce documentation disputes and strengthen buyer confidence.",
      benefits: [
        "Defensible exposure summaries",
        "Buyer-ready documentation",
        "Dispute risk reduction"
      ]
    },
    {
      icon: Snowflake,
      title: "Cold-Chain Operators",
      description: "Improve SLA defensibility and operational visibility.",
      benefits: [
        "Real-time cumulative exposure tracking",
        "SLA compliance documentation",
        "Operational intervention signals"
      ]
    },
    {
      icon: Scale,
      title: "Insurers",
      description: "Standardize inland exposure interpretation and reduce claim ambiguity.",
      benefits: [
        "Structured claim evidence",
        "Standardized inland assessment",
        "Underwriting intelligence"
      ]
    },
    {
      icon: Sprout,
      title: "Producers",
      description: "Ensure produce reaches markets in condition that reflects its quality.",
      benefits: [
        "Reduced post-harvest losses",
        "Market-ready quality assurance",
        "Stronger buyer trust"
      ]
    }
  ];

  return (
    <section id="for-whom" className="py-20 md:py-32 bg-background" data-testid="for-whom-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">For Whom</span>
            <div className="h-px w-16 bg-border" />
          </div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight" data-testid="for-whom-title">
            Built for High-Stakes<br />Supply Chains
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {audiences.map((audience, index) => (
            <div 
              key={index}
              className="bg-card border border-border hover:border-primary/30 transition-colors duration-300 p-6 lg:p-8 flex flex-col"
              data-testid={`audience-card-${index + 1}`}
            >
              <audience.icon className="w-6 h-6 text-primary mb-6" />
              <h3 className="font-display font-bold text-xl text-foreground mb-3">{audience.title}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed mb-6">{audience.description}</p>
              <ul className="space-y-2 mt-auto">
                {audience.benefits.map((benefit, i) => (
                  <li key={i} className="font-body text-xs text-muted-foreground/70 flex items-start gap-2">
                    <Diamond className="w-2 h-2 text-primary mt-1.5 flex-shrink-0 fill-primary" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Intelligence Layer Section
const IntelligenceLayerSection = () => {
  const features = [
    {
      icon: RefreshCw,
      letter: "A",
      title: "Operational Risk Intervention",
      description: "Early risk signals enable route adjustments, expedited delivery, and mitigation before quality thresholds are breached."
    },
    {
      icon: TrendingUp,
      letter: "B",
      title: "Cumulative Risk Forecasting",
      description: "Exposure-based modeling anticipates threshold breaches based on time-temperature trajectory rather than isolated sensor deviations."
    },
    {
      icon: FileCheck,
      letter: "C",
      title: "Structured Exposure Certification",
      description: "Standardized exposure summaries designed to reduce ambiguity in claim assessment and commercial dispute resolution."
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-background" data-testid="intelligence-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Intelligence Layer</span>
            <div className="h-px w-16 bg-border" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="flex flex-col gap-6"
              data-testid={`intelligence-feature-${index + 1}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border border-border flex items-center justify-center">
                  <feature.icon className="w-5 h-5 text-foreground" />
                </div>
                <span className="font-display text-sm text-muted-foreground">{feature.letter}</span>
              </div>
              <h3 className="font-display font-medium text-lg text-foreground">{feature.title}</h3>
              <p className="font-body text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Positioning Section
const PositioningSection = () => {
  const positions = [
    {
      label: "Scalable",
      description: "Software infrastructure that grows with the ecosystem, not constrained by hardware deployment cycles."
    },
    {
      label: "Asset-Light",
      description: "Integration layer model — no capital expenditure on IoT hardware. Connects to existing sensor infrastructure."
    },
    {
      label: "Platform Play",
      description: "The standardized exposure layer becomes foundational to cold-chain accountability infrastructure."
    }
  ];

  return (
    <section className="py-20 md:py-32 bg-background" data-testid="positioning-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Positioning</span>
              <div className="h-px w-16 bg-border" />
            </div>
            
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight leading-[0.95] mb-6" data-testid="positioning-title">
              A Software-First Intelligence Infrastructure
            </h2>
            
            <p className="font-body text-muted-foreground leading-relaxed">
              QuantTechGrid<sup className="text-[0.5em] align-super">™</sup> operates as a software-first environmental exposure intelligence platform. We integrate with external IoT hardware, interpret telemetry through structured modeling, and generate standardized risk documentation — independent of device manufacturer.
            </p>
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            {positions.map((pos, index) => (
              <div 
                key={index}
                className="bg-card border border-border data-line p-6"
                data-testid={`positioning-card-${index + 1}`}
              >
                <span className="font-display font-medium text-sm tracking-widest text-primary uppercase block mb-3">{pos.label}</span>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{pos.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Pilot Request Section
const PilotRequestSection = ({ onRequestPilot }) => {
  return (
    <section id="pilot-request" className="py-20 md:py-32 bg-background" data-testid="pilot-request-section">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left Column */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Pilot Program</span>
              <div className="h-px w-16 bg-border" />
            </div>
            
            <h2 className="font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground uppercase tracking-tight leading-[0.95] mb-6" data-testid="pilot-section-title">
              Request Structured Pilot
            </h2>
            
            <p className="font-body text-muted-foreground leading-relaxed mb-8">
              Submit your request to begin the structured pilot intake process. Our team will assess your supply chain context and respond within 2 business days.
            </p>
            
            <ul className="space-y-4">
              {[
                "Structured onboarding assessment",
                "Commodity-specific exposure modeling setup",
                "Integration pathway review",
                "Pilot documentation framework delivery"
              ].map((item, index) => (
                <li key={index} className="font-body text-sm text-muted-foreground flex items-start gap-3">
                  <Diamond className="w-2 h-2 text-primary mt-2 flex-shrink-0 fill-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column - Form */}
          <PilotRequestForm />
        </div>
      </div>
    </section>
  );
};

// Pilot Request Form Component
const INDUSTRY_OPTIONS = [
  "Exporter",
  "Cold Chain Logistics",
  "Food Distribution",
  "Seafood Processing",
  "Agriculture Aggregator",
  "Insurance",
  "Other"
];

const PilotRequestForm = ({ inModal = false, onSuccess }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    company: "",
    email: "",
    industry: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIndustryChange = (value) => {
    setFormData(prev => ({ ...prev, industry: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.industry) {
      toast.error("Please select an industry");
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/pilot-request`, formData);
      if (response.data.success) {
        toast.success("Request submitted!", {
          description: response.data.message
        });
        setFormData({ full_name: "", company: "", email: "", industry: "", message: "" });
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      toast.error("Submission failed", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-card border border-border p-6 md:p-8" data-testid="pilot-request-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label htmlFor="full_name" className="font-display text-xs tracking-widest text-muted-foreground uppercase mb-2 block">
            Full Name *
          </Label>
          <Input
            id="full_name"
            name="full_name"
            placeholder="Jane Smith"
            value={formData.full_name}
            onChange={handleChange}
            required
            className="bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20"
            data-testid="input-full-name"
          />
        </div>
        <div>
          <Label htmlFor="company" className="font-display text-xs tracking-widest text-muted-foreground uppercase mb-2 block">
            Company *
          </Label>
          <Input
            id="company"
            name="company"
            placeholder="Acme Exports Ltd."
            value={formData.company}
            onChange={handleChange}
            required
            className="bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20"
            data-testid="input-company"
          />
        </div>
      </div>

      <div className="mb-4">
        <Label htmlFor="email" className="font-display text-xs tracking-widest text-muted-foreground uppercase mb-2 block">
          Email Address *
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          placeholder="jane@company.com"
          value={formData.email}
          onChange={handleChange}
          required
          className="bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20"
          data-testid="input-email"
        />
      </div>

      <div className="mb-4">
        <Label htmlFor="industry" className="font-display text-xs tracking-widest text-muted-foreground uppercase mb-2 block">
          Industry *
        </Label>
        <Select value={formData.industry} onValueChange={handleIndustryChange}>
          <SelectTrigger 
            className="bg-background border-input text-foreground focus:border-primary focus:ring-primary/20"
            data-testid="input-industry"
          >
            <SelectValue placeholder="Select your industry" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            {INDUSTRY_OPTIONS.map((industry) => (
              <SelectItem 
                key={industry} 
                value={industry}
                className="text-foreground hover:bg-secondary focus:bg-secondary"
              >
                {industry}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="mb-6">
        <Label htmlFor="message" className="font-display text-xs tracking-widest text-muted-foreground uppercase mb-2 block">
          Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          placeholder="Briefly describe your supply chain context, commodities handled, and current documentation challenges..."
          value={formData.message}
          onChange={handleChange}
          required
          rows={5}
          className="bg-background border-input text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-primary/20 resize-none"
          data-testid="input-message"
        />
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-display font-semibold tracking-wider uppercase py-6 transition-colors disabled:opacity-50"
        data-testid="submit-request-btn"
      >
        {isSubmitting ? "Submitting..." : "Submit Request"}
      </Button>

      <p className="font-body text-xs text-muted-foreground text-center mt-4">
        Your information is handled with strict confidentiality.
      </p>
    </form>
  );
};

// Footer Section
const Footer = ({ onRequestPilot }) => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
      toast.success("Subscribed!", { description: "You'll receive our latest updates." });
    }
  };

  const navColumns = [
    {
      title: "Platform",
      links: [
        { label: "What We Do", action: () => scrollToSection('capabilities') },
        { label: "How It Works", action: () => scrollToSection('process') },
        { label: "Framework", action: () => scrollToSection('afi-index') },
      ]
    },
    {
      title: "Solutions",
      links: [
        { label: "For Exporters", action: () => scrollToSection('for-whom') },
        { label: "For Operators", action: () => scrollToSection('for-whom') },
        { label: "For Insurers", action: () => scrollToSection('for-whom') },
      ]
    },
    {
      title: "Company",
      links: [
        { label: "Request Pilot", action: onRequestPilot },
        { label: "Contact", action: () => scrollToSection('pilot-request') },
        { label: "AFI Index", action: () => scrollToSection('afi-index') },
      ]
    }
  ];

  const legalLinks = [
    "Privacy Policy",
    "Terms of Service", 
    "Cookie Policy",
    "Data Protection"
  ];

  return (
    <footer className="bg-background border-t border-border" data-testid="footer">
      {/* Main Footer Content */}
      <div className="w-full px-8 md:px-16 lg:px-24 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Left Section - Newsletter */}
          <div className="lg:col-span-5">
            <h3 className="font-display font-semibold text-xl text-foreground mb-2">
              Get the latest QuantTechGrid<sup className="text-[0.5em] align-super">™</sup> news
            </h3>
            <p className="font-body text-sm text-muted-foreground mb-6">
              Stay informed on cold-chain intelligence updates and industry insights.
            </p>
            
            <form onSubmit={handleSubscribe} className="mb-6">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <input
                    type="email"
                    placeholder="Enter email here"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-b border-border focus:border-primary py-3 text-foreground placeholder:text-muted-foreground font-body text-sm outline-none transition-colors"
                    data-testid="footer-email-input"
                  />
                </div>
                <button
                  type="submit"
                  className="font-display text-sm text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider whitespace-nowrap"
                  data-testid="footer-subscribe-btn"
                >
                  Subscribe Now
                </button>
              </div>
            </form>

            {/* Social Icons */}
            <div className="flex items-center gap-4 mt-8">
              <a href="#" className="w-10 h-10 border border-border hover:border-muted-foreground flex items-center justify-center transition-colors group" aria-label="LinkedIn" data-testid="social-linkedin">
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a href="#" className="w-10 h-10 border border-border hover:border-muted-foreground flex items-center justify-center transition-colors group" aria-label="X (Twitter)" data-testid="social-twitter">
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="#" className="w-10 h-10 border border-border hover:border-muted-foreground flex items-center justify-center transition-colors group" aria-label="YouTube" data-testid="social-youtube">
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              <a href="#" className="w-10 h-10 border border-border hover:border-muted-foreground flex items-center justify-center transition-colors group" aria-label="Instagram" data-testid="social-instagram">
                <svg className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>

            {/* Copyright */}
            <div className="mt-8">
              <p className="font-body text-xs text-muted-foreground/70">
                © {new Date().getFullYear()} QuantTechGrid™. All Rights Reserved.
              </p>
            </div>
          </div>

          {/* Right Section - Navigation Columns */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {navColumns.map((column, colIndex) => (
                <div key={colIndex}>
                  <h4 className="font-display font-semibold text-sm tracking-wider text-muted-foreground uppercase mb-4">
                    {column.title}
                  </h4>
                  <nav className="flex flex-col gap-3" data-testid={`footer-nav-${column.title.toLowerCase()}`}>
                    {column.links.map((link, linkIndex) => (
                      <button
                        key={linkIndex}
                        onClick={link.action}
                        className="font-body text-sm text-muted-foreground hover:text-foreground transition-colors text-left"
                      >
                        {link.label}
                      </button>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Legal Bar */}
      <div className="border-t border-border">
        <div className="w-full px-8 md:px-16 lg:px-24 py-6">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link, index) => (
              <button
                key={index}
                className="font-body text-xs text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                data-testid={`footer-legal-${index}`}
              >
                {link}
              </button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Pilot Request Modal
const PilotRequestModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pilot-modal">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <span className="font-display font-medium text-sm tracking-widest text-primary uppercase">Pilot Program</span>
          </div>
          <DialogTitle className="font-display font-bold text-2xl md:text-3xl text-foreground uppercase tracking-tight">
            Request Structured Pilot
          </DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <p className="font-body text-muted-foreground text-sm mb-6">
            Submit your request to begin the structured pilot intake process. Our team will assess your supply chain context and respond within 2 business days.
          </p>
          <PilotRequestForm inModal={true} onSuccess={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Inner App Component (needs ThemeProvider context)
function AppContent() {
  const [isPilotModalOpen, setIsPilotModalOpen] = useState(false);

  const openPilotModal = () => setIsPilotModalOpen(true);
  const closePilotModal = () => setIsPilotModalOpen(false);

  return (
    <div className="min-h-screen bg-background transition-colors duration-300" data-testid="app-container">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: 'oklch(var(--card))',
            border: '1px solid oklch(var(--border))',
            color: 'oklch(var(--foreground))'
          }
        }}
      />
      
      <Navigation onRequestPilot={openPilotModal} />
      
      <main>
        <HeroSection onRequestPilot={openPilotModal} />
        <CapabilitiesSection />
        <AccountabilityGapSection />
        <ProcessSection />
        <AFIIndexSection />
        <ForWhomSection />
        <IntelligenceLayerSection />
        <PositioningSection />
        <PilotRequestSection onRequestPilot={openPilotModal} />
      </main>
      
      <Footer onRequestPilot={openPilotModal} />
      
      <PilotRequestModal isOpen={isPilotModalOpen} onClose={closePilotModal} />
    </div>
  );
}

// Main App Component - wraps everything in ThemeProvider
function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
